import type { Channel, DynamicFlatNode, TelemetryDataValues } from 'commons';
/**
 *
 */
export enum NodeTypes {
  DATASOURCE = 'Datasource',
  CHANNEL = 'Channel',
}
/**
 *
 * @param _
 * @param param1
 * @returns
 */
export const nodeIsDatasource = (_: number, { type }: DynamicFlatNode) => type === NodeTypes.DATASOURCE;
/**
 *
 * @param _
 * @param param1
 * @returns
 */
export const nodeIsChannel = (_: number, { type, hidden }: DynamicFlatNode) => type === NodeTypes.CHANNEL && !hidden;
/**
 *
 * @param _
 * @param param1
 * @returns
 */
export const nodeHasChildren = (_: number, { expandable, hidden }: DynamicFlatNode) => expandable && !hidden;
/**
 * Converts to an array of Node for the tree
 * @returns
 */
export const channelListToNodeList = () => (channels: Channel[]) => channels.map((c) => c.toDynamicFlatNode());

type JsonSerializable = Record<string, unknown> | unknown[];

/**
 *
 * @param data
 * @param filename
 */
export const exportDataAsJson = (data: JsonSerializable, filename: string): void => {
  const jsonData = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonData], { type: 'application/json' });
  createAnchorAndDownload(blob, filename);
};
/**
 *
 * @param data
 * @param filename
 */
export const convertJsonToCsvAndExport = (data: string, filename: string): void => {
  const jsonData = convertJsonToCsv(data);
  const blob = new Blob([jsonData], { type: 'application/csv' });
  createAnchorAndDownload(blob, filename);
};
function sortObject(dataObject: TelemetryDataValues): TelemetryDataValues {
  const sortedObject: TelemetryDataValues = { datetime: dataObject.datetime };
  const arr = [];

  for (const key in dataObject) {
    if (Object.hasOwn(dataObject, key)) {
      arr.push(key);
    }
  }

  arr.sort((a, b) => a.localeCompare(b));

  for (const key of arr) {
    sortedObject[key] = dataObject[key];
  }

  return sortedObject;
}
/**
 *
 * @param jsonString
 * @returns
 */
export const convertJsonToCsv = (jsonString: string): string => {
  const dataTable = JSON.parse(jsonString) as [];

  const flatData: TelemetryDataValues[] = dataTable;
  let mergedData: TelemetryDataValues[] = [];
  // flatten data
  if (flatData.length === 0) {
    return '';
  }
  // merge data with same datetime
  flatData.forEach((telemetryDataValues) => {
    const index = mergedData.findIndex((md) => md.datetime === telemetryDataValues.datetime);
    const ordered = Object.keys(telemetryDataValues)
      .sort((a, b) => a.localeCompare(b))
      .reduce(
        (obj: TelemetryDataValues, key) => {
          obj[key] = telemetryDataValues[key];
          return obj;
        },
        { datetime: telemetryDataValues.datetime }
      );
    if (index === -1) {
      mergedData.push(telemetryDataValues);
    }
    // order "fd" object properties by name
    else {
      mergedData[index] = { ...mergedData[index], ...ordered };
    }
  });
  mergedData = mergedData.map((data) => sortObject(data));
  // get header from first entry and concat them with semicolon for csv compatibility
  const header = `${Object?.keys(mergedData[0]).join(',')}\n`;
  // get all values and concat them with semicolon for csv compatibility
  const rows = mergedData.map((telemetryDataValues) => `${Object?.values(telemetryDataValues)}\n`).join('');
  // concat header and rows
  return header + rows;
};
/**
 *  Creates an anchor element and downloads the blob.
 * @param blob  The Blob to download.
 * @param filename  The name of the file to download.
 */
export function createAnchorAndDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
/**
 *
 * @param values
 * @param prefix
 * @returns
 */
export const prefixObjectKeysWith = (values: { [x: string]: unknown }[], prefix: string) =>
  values.map((value) => {
    // replace prefix all keys with the instance label
    Object.keys(value).forEach((key) => {
      if (key === 'datetime') {
        return;
      }
      const newKey = `${prefix}_${key}`;
      value[newKey] = value[key];
      delete value[key];
    });
    return value;
  });
